import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Star, Clock, PlayCircle } from "lucide-react";
import Reveal from "../components/Reveal";
import BlurImage from "../components/BlurImage";
import { useSite } from "../context/SiteContext";
import api from "../lib/api";

export default function Courses() {
    const [courses, setCourses] = useState([]);
    const [filter, setFilter] = useState("All");
    const { openNotify } = useSite();
    const navigate = useNavigate();
    useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)).catch(() => {}); }, []);

    const categories = useMemo(() => ["All", ...Array.from(new Set(courses.map((c) => c.category)))], [courses]);
    const shown = filter === "All" ? courses : courses.filter((c) => c.category === filter);

    return (
        <div data-testid="courses-page">
            <section className="bg-offwhite py-20 lg:py-24 border-b border-neutral-200">
                <div className="mx-container text-center">
                    <p className="overline mb-5">Courses</p>
                    <h1 className="font-display font-black uppercase tracking-tightest text-5xl sm:text-7xl leading-[0.9] max-w-4xl mx-auto">
                        Learn the business<br /><span className="text-brand-yellow">behind the art.</span>
                    </h1>
                    <p className="mt-7 max-w-xl mx-auto text-neutral-500 text-lg">Premium, practical courses on production, marketing, visuals and building a career, taught by working professionals.</p>
                </div>
            </section>

            <section className="py-16 lg:py-20 bg-white">
                <div className="mx-container">
                    <div className="flex flex-wrap gap-2 mb-10">
                        {categories.map((c) => (
                            <button
                                key={c}
                                onClick={() => setFilter(c)}
                                data-testid={`course-filter-${c.toLowerCase().replace(/\s|&/g, "-")}`}
                                className={`px-4 py-2 text-xs font-semibold uppercase tracking-wide transition-colors border ${
                                    filter === c ? "bg-ink text-white border-ink" : "border-neutral-300 text-neutral-500 hover:border-ink"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
                        {shown.map((c, i) => (
                            <Reveal key={c.slug} delay={(i % 3) * 0.05}>
                                <Link to={c.landing || `/courses/${c.slug}`} className="group block card-lift" data-testid={`course-card-${c.slug}`}>
                                    <div className="relative overflow-hidden aspect-[16/10]">
                                        <BlurImage src={c.cover} alt={c.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                        {c.badge && <span className="absolute top-3 left-3 bg-brand-yellow text-ink text-[10px] font-bold uppercase tracking-wide px-2.5 py-1">{c.badge}</span>}
                                        {c.coming_soon && <span className="absolute top-3 right-3 bg-espresso text-white text-[10px] font-bold uppercase tracking-wide px-2.5 py-1" data-testid={`course-comingsoon-${c.slug}`}>Coming Soon</span>}
                                        <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            <PlayCircle className="h-12 w-12 text-white drop-shadow" />
                                        </div>
                                    </div>
                                    <div className="pt-4 pb-6 px-4 text-center">
                                        <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-400">{c.category} · {c.level}</p>
                                        <h3 className="mt-2 font-display font-bold text-xl leading-tight group-hover:text-brand-blue transition-colors">{c.title}</h3>
                                        <p className="mt-1.5 text-sm text-neutral-500 line-clamp-2">{c.excerpt}</p>
                                        <div className="mt-3 flex items-center justify-center gap-3 text-xs text-neutral-500">
                                            {!c.coming_soon && Number(c.reviews) > 0 && <span role="link" tabIndex={0} title="Read the reviews" onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`${c.landing || `/courses/${c.slug}`}#reviews`); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); navigate(`${c.landing || `/courses/${c.slug}`}#reviews`); } }} className="flex items-center gap-1 underline underline-offset-4 decoration-dotted hover:text-ink cursor-pointer" data-testid={`course-rating-${c.slug}`}><Star className="h-3.5 w-3.5 fill-brand-yellow text-brand-yellow" />{c.rating} ({c.reviews})</span>}
                                            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{c.duration}</span>
                                        </div>
                                        {c.coming_soon ? (
                                            <button onClick={(e) => { e.preventDefault(); openNotify({ kind: "course", slug: c.slug, title: c.title }); }} className="mt-3 btn-dark !py-2 !px-4 text-[11px]" data-testid={`course-notify-${c.slug}`}>Notify Me</button>
                                        ) : (
                                            <p className="mt-3 font-display font-bold text-lg">₹{c.price.toLocaleString("en-IN")} {Number(c.original_price) > Number(c.price) && <span className="text-neutral-400 line-through font-normal text-sm">₹{Number(c.original_price).toLocaleString("en-IN")}</span>}</p>
                                        )}
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
